import { Link } from "react-router-dom";
import StickyNavbar from "../navbar";
import QuizPopup from "./QuizPopup";
import React, { useState } from "react";
import { motion } from "framer-motion";
import example1 from "../images/example1.png";
import median from "../images/median.png";
import mode from "../images/mode.png";
import percent from "../images/percent.png";

const DataCollection = () => {
  const [showQuiz, setShowQuiz] = useState(false);

  const handleQuizComplete = (answers) => {
    console.log("User answers:", answers);
  };

  return (
    <div>
      <StickyNavbar />
      <div className="bg-white text-black p-4 lg:mt-10 mt-20 md:text-xl sm:p-10 lg:px-20">
        <div className="space-y-3">
          <h3 className=" font-bold text-[#005FFF] md:text-2xl text-xl my-10">
            Storytelling for qualitative data collection and analysis
          </h3>

          <p>
            Storytelling in qualitative research is about understanding people’s
            experiences by focusing on the stories they share. Rather than
            breaking the data into smaller themes or categories, storytelling
            looks at data as complete pieces of information. It looks at not
            just what participants say, but how they say it—their emotions, the
            events they experience, and the overall meaning of their story.
            Storytelling analysis is particularly helpful when we want to
            understand the lived experiences of people in a way that respects
            and puts their voice at the forefront– meaning it can be very
            valuable when doing research with the community.
          </p>

          <p>By listening to people’s stories, we can:</p>

          <ul className="list-disc list-inside space-y-3">
            <li>Learn how health challenges impact daily life.</li>
            <li>Understand barriers in a more personal way.</li>
            <li>
              Gain perspectives from people which may be difficult to see
              through numbers or brief responses.
            </li>
          </ul>

          <h4 className=" font-bold text-[#005FFF] text-2xl md:pt-10">
            Key points in storytelling:
          </h4>

          <ul className="list-disc list-inside space-y-3">
            <li>
              <strong>Focus on whole ‘narratives’:</strong> Instead of dividing
              responses into separate themes, storytelling looks at the story as
              a whole.
            </li>
            <li>
              <strong>Focus on meaning:</strong> Stories reveal what matters
              most to the person telling them.
            </li>
            <li>
              <strong>Focus on how stories are told:</strong>
              <ul className="list-disc list-inside space-y-3 ml-5">
                <li>The sequence of events (beginning, middle, and end)</li>
                <li>The tone and emotions expressed</li>
                <li>The context or background provided in the story</li>
              </ul>
            </li>
          </ul>

          <p>
            For example, if we are going to interview mothers about their
            journeys to health clinics, we might tell a story about how she
            overcame transportation barriers to get her child vaccinated.
            Storytelling analysis would explore not just the details but also
            her determination, the obstacles she faced, and how this experience
            influenced her views on healthcare.
          </p>

          <h4 className="font-bold text-[#005FFF] text-2xl md:pt-10">
            Why is it a unique approach?
          </h4>

          <ul className="list-disc list-inside space-y-3">
            <li>
              <strong>Rich context:</strong>Stories often provide detailed
              context about a person’s life, making them a valuable source of
              information about broader social or cultural issues.
            </li>
            <li>
              <strong>Human connection:</strong> Stories engage others
              emotionally, helping readers or listeners to empathise with
              participants’ experiences.
            </li>
            <li>
              <strong>The bigger picture:</strong> A single story can highlight
              key issues that other community members may relate to, offering a
              view into shared challenges or solutions.
            </li>
          </ul>

          <h4 className=" font-bold text-[#005FFF] text-2xl md:pt-10">
            A brief guide to Storytelling:
          </h4>

          <ol className="list-decimal list-inside space-y-7 mt-5">
            <li>
              <strong>Prepare your data:</strong>
              <ul className="list-disc list-inside space-y-3 ml-5">
                <li>
                  For example, if you have carried out interviews, you would
                  need to transcribe the interviews first. This means to write
                  out what was said during each interview, so that you can look
                  at it in full.
                </li>
                <li>
                  You can then read through the interviews, and reflect on them.
                  Go through what was said to understand the main points and get
                  a feel for the participant's experiences.
                </li>
              </ul>
            </li>
            <li>
              <strong>Find the main stories:</strong>
              <ul className="list-disc list-inside space-y-3 ml-5">
                <li>
                  Look for key moments: Look for what appears to be the most
                  important events or experiences that are mentioned in the
                  interviews. These might include challenges, changes, or
                  emotional moments.
                </li>
                <li>
                  Spot patterns: Related to this, you need to look for repeated
                  themes; are there challenges that are mentioned again and
                  again? Or did the person you interviewed mention the same
                  person, or place, lots of times?
                </li>
              </ul>
            </li>
            <li>
              <strong>
                Use these elements to break down each participant's story:
              </strong>
              <ul className="list-disc list-inside space-y-3 ml-5">
                <li>Setting: Where and when did things happen?</li>
                <li>Characters: Who was involved in their experience?</li>
                <li>Plot: What happened in their journey?</li>
                <li>Challenges: What problems or barriers did they face?</li>
                <li>
                  Outcome: How did they deal with these, or what did they
                  change?
                </li>
                <li>Themes: What message or lesson does the story reveal?</li>
              </ul>
            </li>
            <li>
              <strong>Write individual stories first: </strong>
              <ul className="list-disc list-inside space-y-3 ml-5">
                <li>
                  Write out a summary for each interview, using the questions
                  above to guide you in breaking down and telling each person’s
                  story. Try to keep it simple and true to the participant’s
                  experience.
                </li>
                <li>
                  Use their words: Include direct quotes from what participants
                  have said because it is important to protect their voice.
                </li>
              </ul>
            </li>
            <li>
              <strong>Look for patterns across stories:</strong>
              <ul className="list-disc list-inside space-y-3 ml-5">
                <li>
                  Try to spot common experiences: for example, look for things
                  that are similar or the same in the challenges or experiences
                  described by different participants. Are lots of the
                  participants mentioning the same things?
                </li>
                <li>
                  Note down differences: We are not just looking for the things
                  that are the same between different participants, we need to
                  note down how their stories differ too, because each person’s
                  experience is unique.
                </li>
              </ul>
            </li>
            <li>
              <strong>Build a bigger picture:</strong>
              <ul className="list-disc list-inside space-y-3 ml-5">
                <li>
                  Tell a collective story: Lets say you have followed these
                  steps and have listened to and broken down three different
                  stories, from interviews carried out with three different
                  participants. You have looked at them separately, and then
                  together, and noted the things they have in common, and where
                  they differ. You can now start to draw together these shared
                  themes and patterns to tell one story that represents overall
                  what you found.
                </li>
              </ul>
            </li>
          </ol>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-10">
          <Link to="/chapter5/sectionTwo">
            <button className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-700">
              Previous
            </button>
          </Link>
          <div className="flex justify-center">
            <button
              onClick={() => setShowQuiz(true)}
              className="bg-white text-blue-800 border border-blue-800 px-3 py-2 rounded-lg"
            >
              Test Your Knowledge
            </button>
          </div>
          <Link to="/chapter6">
            <button className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-700">
              Next
            </button>
          </Link>
          
        </div>
      </div>
      {showQuiz && (
        <QuizPopup onClose={() => setShowQuiz(false)} onComplete={handleQuizComplete} />
      )}
    </div>
  );
};

export default DataCollection;
